import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle, IndianRupee, TrendingUp, Landmark, ArrowUpRight, ArrowDownRight, Minus,
  ShieldCheck, ShieldAlert, Hash, Bot, UserSearch, Loader2, ArrowRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const pct = (n) => (n === null || n === undefined) ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;

const WINDOW_OPTIONS = [
  { key: '1Y', label: '1Y', lastN: 1 },
  { key: '3Y', label: '3Y', lastN: 3 },
  { key: '5Y', label: '5Y', lastN: 5 },
  { key: 'ALL', label: 'All-Time', lastN: 999 },
];

const EXCEPTION_LABELS = {
  arithmetic_mismatch: 'Arithmetic Mismatch',
  unmatched_transaction: 'Unmatched Transaction',
  duplicate_candidate: 'Duplicate Candidate',
  unallocated_amount: 'Unallocated Amount',
  low_confidence_pending_review: 'Low-Confidence Pending Review',
  fy_overlap: 'FY Overlap (Live + Historical)',
};

const Panel = ({ title, subtitle, icon: Icon, action, children, testid }) => (
  <Card data-testid={testid} className="p-5 bg-white rounded-2xl border border-slate-100 shadow-sm">
    <div className="flex items-center justify-between mb-1">
      <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-teal-600" /> : null}{title}
      </h3>
      {action}
    </div>
    {subtitle ? <p className="text-xs text-slate-400 mb-4">{subtitle}</p> : <div className="mb-4" />}
    {children}
  </Card>
);

const StatBlock = ({ label, value, tone = 'slate' }) => {
  const tones = {
    slate: 'text-slate-800', emerald: 'text-emerald-700', amber: 'text-amber-700', rose: 'text-rose-700',
  };
  return (
    <div className="text-center p-3 rounded-xl bg-slate-50">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className={`text-lg font-bold ${tones[tone]}`}>{value}</div>
    </div>
  );
};

const GrowthArrow = ({ value }) => {
  if (value === null || value === undefined) return <Minus className="h-4 w-4 text-slate-300" />;
  if (value > 0) return <ArrowUpRight className="h-4 w-4 text-emerald-600" />;
  if (value < 0) return <ArrowDownRight className="h-4 w-4 text-rose-600" />;
  return <Minus className="h-4 w-4 text-slate-400" />;
};

const ReconstructedAccounts = () => {
  const [exceptions, setExceptions] = useState(null);
  const [multiFy, setMultiFy] = useState(null);
  const [multiFyError, setMultiFyError] = useState(false);
  const [selectedFy, setSelectedFy] = useState('');
  const [fyAggregate, setFyAggregate] = useState(null);
  const [fyLoading, setFyLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [numberingAudit, setNumberingAudit] = useState(null);
  const [aiAudit, setAiAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(true);
  const [windowKey, setWindowKey] = useState('1Y');
  const [windowData, setWindowData] = useState(null);
  const [windowLoading, setWindowLoading] = useState(false);
  const [windowError, setWindowError] = useState(false);
  const [travellerUcc, setTravellerUcc] = useState('');
  const [travellerProfit, setTravellerProfit] = useState(null);
  const [travellerLoading, setTravellerLoading] = useState(false);
  const [travellerError, setTravellerError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [numbering, aiRule] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/audit/numbering-integrity`).catch(() => null),
          axios.get(`${BACKEND_URL}/api/audit/ai-rule-enforcement`).catch(() => null),
        ]);
        if (!alive) return;
        setNumberingAudit(numbering?.data || null);
        setAiAudit(aiRule?.data || null);
      } finally {
        if (alive) setAuditLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [exc, mfy] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/reconciliation/exceptions`).catch(() => ({ data: { total: 0, byType: {}, exceptions: [] } })),
          axios.get(`${BACKEND_URL}/api/profit/multi-fy`, { params: { lastN: 20 } }).catch((e) => ({ error: e })),
        ]);
        if (!alive) return;
        setExceptions(exc.data);
        if (mfy.error) {
          setMultiFyError(true);
        } else {
          setMultiFy(mfy.data);
          const fys = mfy.data?.financialYears || [];
          if (fys.length) setSelectedFy(fys[fys.length - 1]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!selectedFy) { setFyAggregate(null); return; }
    let alive = true;
    setFyLoading(true);
    axios.get(`${BACKEND_URL}/api/profit/aggregate`, { params: { financialYear: selectedFy } })
      .then((r) => { if (alive) setFyAggregate(r.data); })
      .catch(() => { if (alive) setFyAggregate(null); })
      .finally(() => { if (alive) setFyLoading(false); });
    return () => { alive = false; };
  }, [selectedFy]);

  useEffect(() => {
    const opt = WINDOW_OPTIONS.find((w) => w.key === windowKey) || WINDOW_OPTIONS[0];
    let alive = true;
    setWindowLoading(true);
    setWindowError(false);
    axios.get(`${BACKEND_URL}/api/profit/multi-fy`, { params: { lastN: opt.lastN } })
      .then((r) => { if (alive) setWindowData(r.data); })
      .catch(() => { if (alive) { setWindowData(null); setWindowError(true); } })
      .finally(() => { if (alive) setWindowLoading(false); });
    return () => { alive = false; };
  }, [windowKey]);

  const byTypeEntries = useMemo(() => Object.entries(exceptions?.byType || {}), [exceptions]);
  const financialYears = multiFy?.financialYears || [];
  const growth = multiFy?.growth || [];

  const lookupTravellerProfit = async () => {
    const ucc = travellerUcc.trim();
    if (!ucc) return;
    setTravellerLoading(true);
    setTravellerError('');
    setTravellerProfit(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/profit/traveller/${encodeURIComponent(ucc)}`);
      setTravellerProfit(r.data);
    } catch (err) {
      setTravellerError(err?.response?.data?.detail || 'Failed to look up traveller profit');
    } finally {
      setTravellerLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24" data-testid="reconstructed-accounts-loading">
        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reconstructed-accounts-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
          <Landmark className="h-7 w-7 text-teal-600" />Reconstructed Accounts
        </h1>
        <p className="text-slate-500 mt-1">
          Bank/vendor-evidence-reconciled historical books (A2-A11) - a separate lens from the live Ops P&amp;L.
        </p>
      </div>

      {/* Panel 0 - A14 Data Integrity audit tile */}
      <Panel testid="panel-data-integrity" icon={ShieldCheck} title="Data Integrity"
        subtitle="A14 audit — numbering-integrity + AI-rule-enforcement, live from GET /api/audit/*.">
        {auditLoading ? (
          <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="data-integrity-grid">
            <div
              data-testid="data-integrity-numbering-card"
              className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${numberingAudit?.overallClean ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}
            >
              <Hash className={`h-5 w-5 mt-0.5 shrink-0 ${numberingAudit?.overallClean ? 'text-emerald-600' : 'text-rose-600'}`} />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Numbering Integrity</div>
                <div data-testid="data-integrity-numbering-status" className={`text-sm font-bold ${numberingAudit?.overallClean ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {numberingAudit ? (numberingAudit.overallClean ? 'Clean' : 'Issues Found') : 'Unavailable'}
                </div>
                {numberingAudit && !numberingAudit.overallClean && (
                  <div className="text-[11px] text-rose-600 mt-0.5">See numbering audit for details</div>
                )}
              </div>
            </div>
            <div
              data-testid="data-integrity-ai-rule-card"
              className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${aiAudit?.overallClean ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}
            >
              {aiAudit?.overallClean ? (
                <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" />
              ) : (
                <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
              )}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Bot className="h-3 w-3" />AI-Rule Enforcement
                </div>
                <div data-testid="data-integrity-ai-rule-status" className={`text-sm font-bold ${aiAudit?.overallClean ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {aiAudit ? (aiAudit.overallClean ? 'Clean' : `${aiAudit.violationCount} Violation${aiAudit.violationCount === 1 ? '' : 's'}`) : 'Unavailable'}
                </div>
                {aiAudit && !aiAudit.overallClean && (
                  <div className="text-[11px] text-amber-600 mt-0.5" data-testid="data-integrity-ai-rule-hint">
                    Routed to human review queue — not auto-fixed
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Panel>

      {/* Panel 1 - Historical Data Exceptions. Read-only counts only — the
          actionable list (confirm/edit/split/reassign/unallocate/reject)
          now lives in Accounting > Exceptions, so this doesn't duplicate
          it as a second disconnected view. */}
      <Panel testid="panel-historical-exceptions" icon={AlertTriangle} title="Historical Data Exceptions"
        subtitle="Open reconciliation exceptions across the reconstruction pipeline - flags, never auto-resolves."
        action={
          <Link to="/accounting?tab=exceptions" data-testid="go-to-exceptions-tab-link"
            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 hover:text-teal-700">
            Resolve in Accounting <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        }>
        {!exceptions || exceptions.total === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="exceptions-empty-state">
            No open exceptions - reconstructed data is fully reconciled right now.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="exceptions-grid">
            {byTypeEntries.map(([type, count]) => (
              <div key={type} data-testid={`exception-badge-${type}`}
                className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-100">
                <span className="text-xs font-medium text-amber-800 truncate pr-2">{EXCEPTION_LABELS[type] || type}</span>
                <span className="text-sm font-bold text-amber-700 shrink-0">{count}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Panel 2 - Reconstructed Accounts financial position (single FY) */}
      <Panel testid="panel-financial-position" icon={IndianRupee} title="Reconstructed Accounts - Financial Position"
        subtitle="Revenue -> Net Sales -> Gross Profit -> Operating Profit for one financial year, from allocated & reconciled records."
        action={financialYears.length > 0 ? (
          <Select value={selectedFy} onValueChange={setSelectedFy}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="financial-year-select">
              <SelectValue placeholder="Select FY" />
            </SelectTrigger>
            <SelectContent>
              {financialYears.slice().reverse().map((fy) => (
                <SelectItem key={fy} value={fy}>{fy}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}>
        {financialYears.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="financial-position-empty-state">
            No reconstructed historical financial years yet - this fills in once A2+A9 historical records exist.
          </p>
        ) : fyLoading || !fyAggregate ? (
          <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" /></div>
        ) : fyAggregate.tripCount === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="financial-position-no-trips">
            No reconstructed trips recorded yet for {selectedFy}.
          </p>
        ) : (
          <div className="space-y-3" data-testid="financial-position-data">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatBlock label="Revenue" value={inr(fyAggregate.revenue)} />
              <StatBlock label="Gross Profit" value={inr(fyAggregate.grossProfit)} tone="emerald" />
              <StatBlock label="Gross Margin" value={pct(fyAggregate.grossMarginPct)} />
              <StatBlock label="Operating Profit" value={inr(fyAggregate.operatingProfit)} tone="emerald" />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400 px-1">
              <span data-testid="financial-position-trip-count">{fyAggregate.tripCount} reconstructed trip(s) - {fyAggregate.scope?.financialYear}</span>
              {fyAggregate.profitStatus === 'provisional_has_open_exceptions' ? (
                <span className="text-amber-600 font-semibold" data-testid="financial-position-provisional-flag">Provisional - open exceptions present</span>
              ) : (
                <span className="text-emerald-600 font-semibold">Fully reconciled</span>
              )}
            </div>
          </div>
        )}
      </Panel>

      {/* Panel 3 - Reconstructed Accounts growth (multi-FY) */}
      <Panel testid="panel-growth" icon={TrendingUp} title="Reconstructed Accounts - Growth"
        subtitle="FY-over-FY % change in revenue, gross profit and operating profit (whole financial years, most recent 20).">

        {/* A11 — 1Y/3Y/5Y/All-Time rolled-up totals. Additive: reuses the
            existing GET /api/profit/multi-fy?lastN= endpoint (which already
            sums revenue/grossProfit/operatingProfit across whichever FYs it
            is given) — just a window selector + a second call to the same
            endpoint with a different lastN, no new backend logic. */}
        <div className="mb-5" data-testid="window-rollup-section">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rolled-Up Totals</span>
            <div className="flex gap-1" data-testid="window-selector">
              {WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  data-testid={`window-btn-${opt.key}`}
                  onClick={() => setWindowKey(opt.key)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    windowKey === opt.key ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {windowLoading ? (
            <div className="flex justify-center py-6"><div className="w-5 h-5 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" /></div>
          ) : windowError || !windowData || !windowData.financialYears?.length ? (
            <p className="text-sm text-slate-400 py-4 text-center" data-testid="window-rollup-empty-state">
              No reconstructed financial years in this window yet.
            </p>
          ) : (
            <div className="space-y-2" data-testid="window-rollup-data">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatBlock label="Revenue" value={inr(windowData.revenue)} />
                <StatBlock label="Gross Profit" value={inr(windowData.grossProfit)} tone="emerald" />
                <StatBlock label="Operating Profit" value={inr(windowData.operatingProfit)} tone="emerald" />
                <StatBlock label="Trips" value={windowData.tripCount} />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400 px-1 flex-wrap gap-1">
                <span data-testid="window-rollup-fy-list">
                  {windowData.financialYears.length} FY{windowData.financialYears.length > 1 ? 's' : ''} in window: {
                    windowData.financialYears.length > 5
                      ? `${windowData.financialYears.slice(0, 5).join(', ')} + ${windowData.financialYears.length - 5} more`
                      : windowData.financialYears.join(', ')
                  }
                </span>
                <span data-testid="window-rollup-traveller-avg">
                  {windowData.reconstructedTravellerCount} traveller(s) · avg trip {inr(windowData.avgTripValue)}
                </span>
              </div>
            </div>
          )}
        </div>

        {multiFyError || financialYears.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="growth-empty-state">
            No reconstructed historical financial years yet - growth needs at least 2 to compare.
          </p>
        ) : growth.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="growth-insufficient-data">
            Only {financialYears.length} financial year reconstructed so far - need at least 2 to show growth.
          </p>
        ) : (
          <div className="space-y-2" data-testid="growth-data">
            {growth.map((g) => (
              <div key={`${g.fromFinancialYear}-${g.toFinancialYear}`} data-testid={`growth-row-${g.fromFinancialYear}-${g.toFinancialYear}`}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50">
                <div className="text-sm font-medium text-slate-700">{g.fromFinancialYear} -&gt; {g.toFinancialYear}</div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="flex items-center gap-1 text-slate-600">Revenue <GrowthArrow value={g.revenueGrowthPct} />{pct(g.revenueGrowthPct)}</span>
                  <span className="flex items-center gap-1 text-slate-600">Gross Profit <GrowthArrow value={g.grossProfitGrowthPct} />{pct(g.grossProfitGrowthPct)}</span>
                  <span className="flex items-center gap-1 text-slate-600">Op. Profit <GrowthArrow value={g.operatingProfitGrowthPct} />{pct(g.operatingProfitGrowthPct)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Step 2 (RBAC decision) — Traveller Profit lookup, reconstructed
          books only. Deliberately placed HERE (Reconstructed Accounts,
          canReports/require_accounts) rather than a new screen shell, and
          NEVER merged into the Agent-visible Travellers list/detail LTV
          screens — see PRD.md Step 2 audit note. */}
      <Panel testid="panel-traveller-profit" icon={UserSearch} title="Traveller Profit - Reconstructed Books"
        subtitle="Admin + Accounts only. Reconstructed-books profit for one traveller (historical trip records linked to a UCC) — a separate lens from the live-CRM LTV figures on the Travellers screens.">
        <div className="flex items-center gap-2 mb-4">
          <Input
            data-testid="traveller-profit-ucc-input"
            placeholder="Enter traveller UCC (e.g. UC1094)"
            value={travellerUcc}
            onChange={(e) => setTravellerUcc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') lookupTravellerProfit(); }}
            className="max-w-xs"
          />
          <Button size="sm" onClick={lookupTravellerProfit} disabled={travellerLoading || !travellerUcc.trim()}
            data-testid="traveller-profit-lookup-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            {travellerLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <UserSearch className="h-3.5 w-3.5 mr-1" />}
            Look up
          </Button>
        </div>

        {travellerError ? (
          <p className="text-sm text-rose-500 py-2" data-testid="traveller-profit-error">{travellerError}</p>
        ) : !travellerProfit ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="traveller-profit-empty-state">
            Enter a UCC and click Look up to see this traveller's reconstructed-books profit.
          </p>
        ) : travellerProfit.tripCount === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="traveller-profit-no-historical-trips">
            No historical (pre-CRM) trips found for {travellerProfit.ucc} — nothing to reconcile yet.
          </p>
        ) : (
          <div className="space-y-3" data-testid="traveller-profit-data">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">{travellerProfit.ucc} · {travellerProfit.tripCount} historical trip(s)</span>
              {travellerProfit.profitStatus === 'final_reconciled' ? (
                <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="traveller-profit-status-final">
                  <ShieldCheck className="h-3.5 w-3.5" />Final Reconciled
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="traveller-profit-status-provisional">
                  <ShieldAlert className="h-3.5 w-3.5" />Provisional - Open Exceptions
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatBlock label="Revenue" value={inr(travellerProfit.revenue)} />
              <StatBlock label="Net Sales" value={inr(travellerProfit.netSales)} />
              <StatBlock label="Gross Profit" value={inr(travellerProfit.grossProfit)} tone="emerald" />
              <StatBlock label="Gross Margin" value={travellerProfit.grossMarginPct === null ? '—' : `${travellerProfit.grossMarginPct}%`} />
              <StatBlock label="Operating Profit" value={inr(travellerProfit.operatingProfit)} tone="emerald" />
              <StatBlock label="Profit Margin" value={travellerProfit.profitMarginPct === null ? '—' : `${travellerProfit.profitMarginPct}%`} />
            </div>
            <div className="text-xs text-slate-400 px-1">FY(s): {travellerProfit.financialYears.join(', ') || '—'}</div>
            {travellerProfit.tripsWithOpenExceptions.length > 0 && (
              <div className="text-xs text-amber-600 px-1" data-testid="traveller-profit-open-exceptions">
                {travellerProfit.tripsWithOpenExceptions.length} trip(s) have open reconciliation flags (unverified contributing records) — figures above are provisional.
              </div>
            )}

            {/* Step 3 — live+historical LTV join (additive, read-only on the live side) */}
            <div className="mt-2 pt-3 border-t border-slate-100" data-testid="traveller-profit-lifetime-ltv">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Lifetime LTV (Live + Historical)</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatBlock label="Live LTV" value={inr(travellerProfit.liveClientTransactionLtv)} />
                <StatBlock label="Historical Revenue" value={inr(travellerProfit.revenue)} />
                <StatBlock
                  label="Lifetime LTV"
                  value={travellerProfit.lifetimeLtvStatus === 'blocked_fy_overlap' ? '—' : inr(travellerProfit.lifetimeLtv)}
                  tone={travellerProfit.lifetimeLtvStatus === 'blocked_fy_overlap' ? 'rose' : 'emerald'}
                />
              </div>
              {travellerProfit.lifetimeLtvStatus === 'blocked_fy_overlap' ? (
                <div className="text-xs text-rose-500 mt-2 flex items-center gap-1" data-testid="traveller-profit-fy-overlap-warning">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Live and historical trips share FY({travellerProfit.fyOverlapWithLive.join(', ')}) for this traveller — lifetime LTV withheld to avoid double-counting. Needs manual review.
                </div>
              ) : (
                <div className="text-xs text-slate-400 mt-2">
                  Live FY(s): {travellerProfit.liveFinancialYears.join(', ') || '—'} · no overlap with historical FY(s) — safe to sum.
                </div>
              )}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default ReconstructedAccounts;
